/** Shared display helpers for dashboard project header (V3 hero + split layouts). */

export function primaryContactDisplayName(details: unknown, fallback?: string | null): string {
    const d = details as { Customers?: Record<string, unknown>[] } | null | undefined;
    const primary =
        d?.Customers?.find((c) => c?.IsPrimaryContact || c?.isPrimaryContact) ?? d?.Customers?.[0];
    if (!primary) return fallback?.trim() || '—';
    const last = String(primary.LastName ?? primary.lastName ?? '').trim();
    const first = String(primary.FirstName ?? primary.firstName ?? '').trim();
    const commaForm = [last, first].filter(Boolean).join(', ');
    if (commaForm) return commaForm;
    return `${first} ${last}`.trim() || fallback?.trim() || '—';
}

/** First-name-first display for hero header (e.g. "Paul Mark"). */
export function primaryContactFriendlyName(details: unknown, fallback?: string | null): string {
    const d = details as { Customers?: Record<string, unknown>[] } | null | undefined;
    const primary =
        d?.Customers?.find((c) => c?.IsPrimaryContact || c?.isPrimaryContact) ?? d?.Customers?.[0];
    if (!primary) return fallback?.trim() || '—';
    const first = String(primary.FirstName ?? primary.firstName ?? '').trim();
    const last = String(primary.LastName ?? primary.lastName ?? '').trim();
    const friendly = `${first} ${last}`.trim();
    if (friendly) return friendly;
    return primaryContactDisplayName(details, fallback);
}

export function formatAddress(details: unknown, fallback?: string | null): string {
    const d = details as {
        InstallationAddress?: Record<string, unknown>;
    } | null | undefined;
    const install = d?.InstallationAddress;
    if (install) {
        const street = install.Street ?? install.street ?? '';
        const city = install.City ?? install.city ?? '';
        const state = install.stateCode ?? install.State ?? install.state ?? '';
        const zip = install.ZipCode ?? install.zipCode ?? '';
        const cityState = [city, state].filter(Boolean).join(', ');
        const line = [street, cityState, zip].filter(Boolean).join(' ');
        if (String(line).trim()) return String(line).trim();
    }
    return fallback?.trim() || '—';
}

export function customerInitials(name: string): string {
    const cleaned = name?.trim() || '';
    if (!cleaned || cleaned === '…' || cleaned === '—') return '?';
    if (cleaned.includes(',')) {
        const [last, first] = cleaned.split(',').map((p) => p.trim());
        return `${(last?.[0] ?? '').toUpperCase()}${(first?.[0] ?? '').toUpperCase()}`.trim() || '?';
    }
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

export function contactRecordInitials(firstName: string, lastName: string): string {
    const ini = `${String(firstName).trim()[0] ?? ''}${String(lastName).trim()[0] ?? ''}`.toUpperCase();
    return ini || '?';
}

export function contactRecordFriendlyName(firstName: string, lastName: string): string {
    return `${String(firstName).trim()} ${String(lastName).trim()}`.trim() || '—';
}

export function contactRecordCommaName(firstName: string, lastName: string): string {
    const last = String(lastName).trim();
    const first = String(firstName).trim();
    const commaForm = [last, first].filter(Boolean).join(', ');
    return commaForm || contactRecordFriendlyName(firstName, lastName);
}

export function formatPhone(phone: string): string {
    const digits = String(phone ?? '').replace(/\D/g, '');
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return String(phone ?? '').trim() || '—';
}

/** Split "street city, ST zip" for compact two-line address display. */
export function splitServiceAddressLine(line: string): { primary: string; secondary: string } {
    const trimmed = line?.trim() || '—';
    if (trimmed === '—') return { primary: '—', secondary: '' };
    const zipMatch = trimmed.match(/^(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (zipMatch) {
        return {
            primary: zipMatch[1].trim(),
            secondary: `${zipMatch[2]} ${zipMatch[3]}`,
        };
    }
    const comma = trimmed.indexOf(',');
    if (comma > 0) {
        return {
            primary: trimmed.slice(0, comma).trim(),
            secondary: trimmed.slice(comma + 1).trim(),
        };
    }
    return { primary: trimmed, secondary: '' };
}
